import { ref, watch, computed, toValue } from 'vue';

import { useCatalog } from './useCatalog';
import { useParseUtils } from './useParseUtils';

const { parseBounds } = useParseUtils();

const productMetadata = ref(null);

let loadingPromise = null;

export function useProductMetadata(region, product) {
    const _region = computed(() => toValue(region));
    const _product = computed(() => toValue(product));

    const { getProductMetadata } = useCatalog();

    async function load() {
        if (loadingPromise) {
            return loadingPromise;
        }
        if (!_region.value) {
            productMetadata.value = null;
            return null;
        }

        loadingPromise = getProductMetadata(_region.value, _product.value)
            .then((value) => {
                productMetadata.value = value;
            })
            .catch((error) => {
                console.error(`Failed to load product '${_region.value} ${_product.value}' metadata`, error);
            });

        return loadingPromise;
    }

    const productDescription = computed(() => {
        return productMetadata.value?.description || null;
    });
    const productBounds = computed(() => {
        try {
            return productMetadata.value?.bounds ? parseBounds(productMetadata.value?.bounds) : null;
        } catch (error) {
            console.warn(`Failed to parse product '${_region.value} ${_product.value}' bounds`, error);
            return null;
        }
    });
    const productFormat = computed(() => {
        return productMetadata.value?.format || null;
    });
    const productMaxZoom = computed(() => {
        try {
            return parseInt(productMetadata.value?.maxzoom) || null;
        } catch (error) {
            console.warn(`Failed to parse product '${_region.value} ${_product.value}' maxzoom`, error);
            return null;
        }
    });
    const productMinZoom = computed(() => {
        try {
            return parseInt(productMetadata.value?.minzoom) || null;
        } catch (error) {
            console.warn(`Failed to parse product '${_region.value} ${_product.value}' minzoom`, error);
            return null;
        }
    });
    const productType = computed(() => {
        return productMetadata.value?.type || null;
    });
    watch(
        [_region, _product],
        async ([r, p]) => {
            productMetadata.value = null;
            loadingPromise = null;

            if (r && p) {
                await load();
            }
        },
        { immediate: true },
    );

    return {
        productMetadata,

        productDescription,
        productBounds,
        productFormat,
        productMaxZoom,
        productMinZoom,
        productType,
    };
}
